/**
 * Neyra — monetization layer (wallet, transactions, payout methods, boosts,
 * premium, verification). Pure localStorage + event bus for reactivity.
 */
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";

const fireMirror = (table, row) => { try { mirror(table, row); } catch {} };

const BUS_EVT = "neara-mone-change";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeMone = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};
const write = (k, v) => {
    localStorage.setItem(k, JSON.stringify(v));
    emit();
};

const K = (userId, s) => `neara.${s}.${userId}`;

/* ---------------- Wallet ---------------- */

const defaultWallet = () => ({ available: 0, pending: 0 });

export const getWallet = (userId) => {
    const w = read(K(userId, "wallet"), null) || defaultWallet();
    return { ...w, total: (w.available || 0) + (w.pending || 0) };
};

/**
 * New users start at ₦0. We only seed demo data for existing premium-style
 * accounts so the wallet feels alive on those. Free new users see a clean
 * empty state — they can only build balance through payments / refunds.
 */
export const seedWalletOnce = (userId, opts = {}) => {
    if (localStorage.getItem(K(userId, "wallet.seeded"))) return;
    if (!opts.demo) {
        // Always start at ₦0 for new accounts
        write(K(userId, "wallet"), { available: 0, pending: 0 });
        write(K(userId, "transactions"), []);
        localStorage.setItem(K(userId, "wallet.seeded"), "1");
        return;
    }
    const now = Date.now();
    const txns = [
        { id: "t1", type: "credit", kind: "service",  title: "Luxe Makeup booking", amount: 25000, at: now - 60_000 * 60 * 3 },
        { id: "t2", type: "credit", kind: "event",    title: "Art & Wine ticket",    amount: 8000,  at: now - 60_000 * 60 * 20 },
        { id: "t3", type: "debit",  kind: "fee",      title: "Platform fee",         amount: 850,   at: now - 60_000 * 60 * 20 },
        { id: "t4", type: "credit", kind: "service",  title: "Tech Services job",    amount: 12000, at: now - 86_400_000 * 2 },
        { id: "t5", type: "debit",  kind: "withdraw", title: "Withdrawal to bank",   amount: 20000, at: now - 86_400_000 * 4, reference: "NEA-WD-XA91Z2" },
    ];
    write(K(userId, "transactions"), txns);
    write(K(userId, "wallet"), { available: 24150, pending: 4500 });
    localStorage.setItem(K(userId, "wallet.seeded"), "1");
};

/* ---------------- Escrow (pending → available) ---------------- */

/** Buyer paid → funds park in seller's pending balance. */
export const addPendingFunds = (sellerId, amount, meta = {}) => {
    if (!sellerId || !amount) return;
    const w = read(K(sellerId, "wallet"), defaultWallet());
    w.pending = (w.pending || 0) + Number(amount);
    write(K(sellerId, "wallet"), w);
    fireMirror(SUPABASE_TABLES.WALLETS, { id: sellerId, userId: sellerId, ...w, updatedAt: new Date().toISOString() });
    addTransaction(sellerId, {
        type: "credit",
        kind: meta.kind || "escrow_in",
        title: meta.title || "Pending — escrow hold",
        amount: Number(amount),
        escrowId: meta.escrowId || null,
        status: "pending",
    });
};

/** Release escrow → pending becomes available. */
export const releaseFunds = (sellerId, amount, meta = {}) => {
    if (!sellerId || !amount) return;
    const w = read(K(sellerId, "wallet"), defaultWallet());
    const release = Math.min(Number(amount), w.pending || 0);
    w.pending = (w.pending || 0) - release;
    w.available = (w.available || 0) + release;
    write(K(sellerId, "wallet"), w);
    fireMirror(SUPABASE_TABLES.WALLETS, { id: sellerId, userId: sellerId, ...w, updatedAt: new Date().toISOString() });
    addTransaction(sellerId, {
        type: "credit",
        kind: "escrow_release",
        title: meta.title || "Released from escrow",
        amount: release,
        escrowId: meta.escrowId || null,
        status: "released",
    });
};

/** Refund credit → goes straight to AVAILABLE balance (withdrawable). */
export const addRefund = (userId, amount, meta = {}) => {
    if (!userId || !amount) return;
    const w = read(K(userId, "wallet"), defaultWallet());
    w.available = (w.available || 0) + Number(amount);
    write(K(userId, "wallet"), w);
    fireMirror(SUPABASE_TABLES.WALLETS, { id: userId, userId, ...w, updatedAt: new Date().toISOString() });
    addTransaction(userId, {
        type: "credit",
        kind: "refund",
        title: meta.title || "Refund credited",
        amount: Number(amount),
        reference: "NEA-RF-" + Math.random().toString(36).slice(2, 8).toUpperCase(),
        eventId: meta.eventId || null,
    });
};

/* ---------------- Paid event tickets (for refund tracking) ---------------- */

/** Records a paid ticket purchase against the buyer for refund flows. */
export const recordPaidTicket = (userId, ticket) => {
    if (!userId || !ticket?.eventId) return;
    const list = read(K(userId, "tickets"), []);
    if (list.some((t) => t.eventId === ticket.eventId)) return;
    list.unshift({
        id: "tk_" + Date.now(),
        eventId: ticket.eventId,
        title: ticket.title || "Event ticket",
        amount: Number(ticket.amount) || 0,
        sellerId: ticket.sellerId || null,
        refundPolicy: ticket.refundPolicy || "refundable",
        refunded: false,
        at: Date.now(),
    });
    write(K(userId, "tickets"), list);
};

export const getPaidTicket = (userId, eventId) =>
    read(K(userId, "tickets"), []).find((t) => t.eventId === eventId) || null;

export const isTicketRefunded = (userId, eventId) =>
    !!getPaidTicket(userId, eventId)?.refunded;

/** Refund a paid ticket → credits buyer's available wallet, marks refunded. */
export const refundPaidTicket = (userId, eventId, meta = {}) => {
    const list = read(K(userId, "tickets"), []);
    const idx = list.findIndex((t) => t.eventId === eventId);
    if (idx < 0) return { ok: false, error: "Ticket not found" };
    const t = list[idx];
    if (t.refunded) return { ok: false, error: "Already refunded" };
    list[idx] = { ...t, refunded: true, refundedAt: Date.now() };
    write(K(userId, "tickets"), list);
    addRefund(userId, t.amount, {
        title: meta.title || `Refund — ${t.title}`,
        eventId,
    });
    return { ok: true, amount: t.amount };
};

/* ---------------- Payment PIN ---------------- */

const pinKey = (userId) => K(userId, "paymentPin");

/** Tiny obfuscation — local only, never used for real auth. */
const obfuscate = (raw) =>
    btoa(String(raw)).split("").reverse().join("");

export const hasPaymentPin = (userId) => !!localStorage.getItem(pinKey(userId));

export const setPaymentPin = (userId, pin) => {
    if (!/^\d{4,6}$/.test(String(pin))) return { ok: false, error: "PIN must be 4–6 digits" };
    localStorage.setItem(pinKey(userId), obfuscate(pin));
    emit();
    return { ok: true };
};

export const verifyPaymentPin = (userId, pin) => {
    const stored = localStorage.getItem(pinKey(userId));
    if (!stored) return { ok: false, error: "PIN not set" };
    if (stored !== obfuscate(pin)) return { ok: false, error: "Incorrect PIN" };
    return { ok: true };
};

export const resetPaymentPin = (userId, newPin) => {
    if (!/^\d{4,6}$/.test(String(newPin))) return { ok: false, error: "PIN must be 4–6 digits" };
    localStorage.setItem(pinKey(userId), obfuscate(newPin));
    emit();
    return { ok: true };
};

/* ---------------- 2FA toggle (mock) ---------------- */

const twoFAKey = (userId) => K(userId, "twoFactor");

export const getTwoFactorEnabled = (userId) =>
    localStorage.getItem(twoFAKey(userId)) === "1";

export const setTwoFactorEnabled = (userId, enabled) => {
    if (enabled) localStorage.setItem(twoFAKey(userId), "1");
    else localStorage.removeItem(twoFAKey(userId));
    emit();
};

/* ---------------- Free/Premium listing limits ---------------- */

export const FREE_LIMITS = {
    services: 2,
    events:   2,
};

/** True if user can create another listing of this kind. */
export const canCreateMore = (user, kind, currentCount) => {
    if (!user) return false;
    if (user.premium) return true; // unlimited for premium
    const cap = FREE_LIMITS[kind] ?? Infinity;
    return currentCount < cap;
};

export const getTransactions = (userId) => read(K(userId, "transactions"), []);

export const addTransaction = (userId, txn) => {
    const list = getTransactions(userId);
    const record = { id: "t_" + Date.now(), at: Date.now(), userId, ...txn };
    list.unshift(record);
    write(K(userId, "transactions"), list);
    fireMirror(SUPABASE_TABLES.TRANSACTIONS, record);
};

/* ---------------- Payout methods ---------------- */

export const getPayoutMethods = (userId) => read(K(userId, "payouts"), []);

export const addPayoutMethod = (userId, pm) => {
    const list = getPayoutMethods(userId);
    const first = list.length === 0;
    list.push({
        id: "pm_" + Date.now(),
        createdAt: Date.now(),
        isPrimary: first,
        ...pm,
    });
    write(K(userId, "payouts"), list);
};

export const removePayoutMethod = (userId, id) => {
    const list = getPayoutMethods(userId).filter((p) => p.id !== id);
    if (list.length && !list.some((p) => p.isPrimary)) list[0].isPrimary = true;
    write(K(userId, "payouts"), list);
};

export const setPrimaryPayout = (userId, id) => {
    const list = getPayoutMethods(userId).map((p) => ({ ...p, isPrimary: p.id === id }));
    write(K(userId, "payouts"), list);
};

/* ---------------- Withdraw ---------------- */

const ADMIN_WITHDRAWALS_KEY = "neyra.withdrawals";
const readWithdrawals = () => read(ADMIN_WITHDRAWALS_KEY, []);
const writeWithdrawals = (v) => write(ADMIN_WITHDRAWALS_KEY, v);

export const withdraw = (userId, amount, pmId) => {
    const wallet = read(K(userId, "wallet"), defaultWallet());
    if (amount <= 0) return { ok: false, error: "Enter an amount" };
    if (amount > (wallet.available || 0)) return { ok: false, error: "Insufficient available balance" };
    const pm = getPayoutMethods(userId).find((p) => p.id === pmId);
    if (!pm) return { ok: false, error: "Select a payout method" };

    wallet.available = (wallet.available || 0) - amount;
    write(K(userId, "wallet"), wallet);
    fireMirror(SUPABASE_TABLES.WALLETS, { id: userId, userId, ...wallet, updatedAt: new Date().toISOString() });
    const ref = "NEA-WD-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    addTransaction(userId, {
        type: "debit",
        kind: "withdraw",
        title: `Withdrawal to ${pm.bankName}`,
        amount,
        reference: ref,
    });

    // Record withdrawal request for admin review.
    const wdId = "wd_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    const wdRecord = {
        id: wdId,
        userId,
        amount,
        bankName: pm.bankName,
        accountNumber: pm.accountNumber,
        accountName: pm.accountName,
        reference: ref,
        status: "pending",
        at: Date.now(),
    };
    const list = readWithdrawals();
    list.unshift(wdRecord);
    writeWithdrawals(list);
    fireMirror(SUPABASE_TABLES.WITHDRAWALS, wdRecord);

    return { ok: true, reference: ref };
};

/* ---------------- Boosts ---------------- */

const BOOST_DURATIONS = {
    "24h": 24 * 60 * 60 * 1000,
    "3d":  3  * 24 * 60 * 60 * 1000,
    "7d":  7  * 24 * 60 * 60 * 1000,
};
export const BOOST_PLANS = [
    { id: "24h", label: "24 hours", price: 500 },
    { id: "3d",  label: "3 days",   price: 1500 },
    { id: "7d",  label: "7 days",   price: 3000 },
];
export const BOOST_GOALS = [
    { id: "views",    label: "More views" },
    { id: "messages", label: "More messages" },
    { id: "joins",    label: "More joins" },
    { id: "requests", label: "More service requests" },
];

export const getBoosts = (userId) => read(K(userId, "boosts"), {});

export const createBoost = (userId, entityKind, entityId, planId, goal) => {
    const plan = BOOST_PLANS.find((p) => p.id === planId);
    if (!plan) return { ok: false, error: "Invalid plan" };
    const now = Date.now();
    const boost = {
        id: `boost_${entityKind}_${entityId}_${now}`,
        userId,
        entityKind,
        entityId,
        planId,
        goal,
        startAt: now,
        endAt: now + BOOST_DURATIONS[planId],
        views: 0,
        clicks: 0,
        messages: 0,
    };
    const boosts = getBoosts(userId);
    boosts[`${entityKind}:${entityId}`] = boost;
    write(K(userId, "boosts"), boosts);
    fireMirror(SUPABASE_TABLES.BOOSTS, boost);
    addTransaction(userId, {
        type: "debit",
        kind: "boost",
        title: `Boost (${plan.label}) — ${entityKind}`,
        amount: plan.price,
    });
    return { ok: true, boost, reference: "NEA-BO-" + Math.random().toString(36).slice(2, 8).toUpperCase() };
};

/** Extend an active or expired boost — adds the chosen plan's duration. */
export const extendBoost = (userId, entityKind, entityId, planId) => {
    const plan = BOOST_PLANS.find((p) => p.id === planId);
    if (!plan) return { ok: false, error: "Invalid plan" };
    const boosts = getBoosts(userId);
    const key = `${entityKind}:${entityId}`;
    const cur = boosts[key];
    const now = Date.now();
    const baseEnd = cur && cur.endAt > now ? cur.endAt : now;
    const next = {
        ...(cur || { id: `boost_${entityKind}_${entityId}_${now}`, userId, entityKind, entityId, goal: cur?.goal || "views", startAt: now, views: 0, clicks: 0, messages: 0 }),
        planId,
        endAt: baseEnd + BOOST_DURATIONS[planId],
        updatedAt: now,
    };
    boosts[key] = next;
    write(K(userId, "boosts"), boosts);
    fireMirror(SUPABASE_TABLES.BOOSTS, next);
    addTransaction(userId, {
        type: "debit",
        kind: "boost_extend",
        title: `Boost extend (${plan.label}) — ${entityKind}`,
        amount: plan.price,
    });
    return { ok: true, boost: next, reference: "NEA-BO-" + Math.random().toString(36).slice(2, 8).toUpperCase() };
};

export const getBoost = (userId, entityKind, entityId) =>
    (getBoosts(userId) || {})[`${entityKind}:${entityId}`] || null;

export const isBoostActive = (userIdOrBoost, entityKind, entityId) => {
    // Overloaded: accepts a boost object directly OR (userId, kind, id).
    if (typeof userIdOrBoost === "object" && userIdOrBoost) {
        return !!(userIdOrBoost.endAt > Date.now());
    }
    const b = getBoosts(userIdOrBoost)[`${entityKind}:${entityId}`];
    return !!(b && b.endAt > Date.now());
};

/* ---------------- Blocks ---------------- */

export const getBlocked = (userId) => read(K(userId, "blocked"), []);
export const isUserBlocked = (userId, peerId) => {
    if (!userId || !peerId) return false;
    return getBlocked(userId).some((b) => b.id === peerId);
};
export const toggleBlocked = (userId, blockedUser) => {
    const list = getBlocked(userId);
    const idx = list.findIndex((b) => b.id === blockedUser.id);
    if (idx >= 0) list.splice(idx, 1);
    else list.unshift({ ...blockedUser, at: Date.now() });
    write(K(userId, "blocked"), list);
};

/* ---------------- Emergency contacts ---------------- */

export const getEmergencyContacts = (userId) => read(K(userId, "emergency"), []);
export const addEmergencyContact = (userId, contact) => {
    const list = getEmergencyContacts(userId);
    list.push({ id: "ec_" + Date.now(), ...contact });
    write(K(userId, "emergency"), list);
};
export const removeEmergencyContact = (userId, id) =>
    write(K(userId, "emergency"), getEmergencyContacts(userId).filter((c) => c.id !== id));

/* ---------------- Banks (Nigeria) ---------------- */

export const NG_BANKS = [
    { id: "gtb",    name: "GTBank",           color: "#F7571A" },
    { id: "accs",   name: "Access Bank",      color: "#0070C0" },
    { id: "zen",    name: "Zenith Bank",      color: "#E10915" },
    { id: "fbn",    name: "First Bank",       color: "#003366" },
    { id: "uba",    name: "UBA",              color: "#D20312" },
    { id: "fcmb",   name: "FCMB",             color: "#5D1F5D" },
    { id: "stanbic",name: "Stanbic IBTC",     color: "#002D72" },
    { id: "opay",   name: "OPay",             color: "#16C65A" },
    { id: "kuda",   name: "Kuda Bank",        color: "#40196D" },
    { id: "monies", name: "Moniepoint MFB",   color: "#1A36E0" },
    { id: "wema",   name: "Wema Bank",        color: "#6B1D5C" },
    { id: "fidelity",name: "Fidelity Bank",   color: "#004A99" },
];

const FAKE_FIRST = ["Ada", "Tunde", "Kemi", "Dami", "Seun", "Ngozi", "Chidi", "Ibrahim", "Lola", "Bola"];
const FAKE_LAST  = ["Okafor", "Bello", "Adewale", "Johnson", "Cole", "Eze", "Okonkwo", "Abdullahi", "Adetayo", "Ade"];

export const mockAccountNameLookup = (accountNumber) =>
    new Promise((resolve) => {
        setTimeout(() => {
            const n = String(accountNumber || "");
            if (!/^\d{10}$/.test(n)) {
                resolve({ ok: false, error: "Account number must be 10 digits" });
                return;
            }
            // Deterministic "looked up" name based on account number
            let h = 0;
            for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) >>> 0;
            const name = `${FAKE_FIRST[h % FAKE_FIRST.length]} ${FAKE_LAST[Math.floor(h / 7) % FAKE_LAST.length]}`;
            resolve({ ok: true, name: name.toUpperCase() });
        }, 800);
    });
